import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useAnimationControls } from "framer-motion";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useShapeWebSound } from "../components/games/shape-web-defender/SoundManager";
import {
  applyDifficultyAfterRound,
  CENTER,
  CLOSE_CALL_PROGRESS,
  DANGER_PROGRESS,
  isUrgent,
  makeSpider,
  openingSpiders,
  pointsForStreak,
  readLevel,
  readLocalBest,
  readRatings,
  SHAPES,
  shapeMeta,
  spawnInterval,
  spiderDisplayPosition,
  spiderFleePoint,
  timeLabel,
  tuningForLevel,
  writeLevel,
  writeLocalBest,
  writeRatings,
} from "../components/games/shape-web-defender/gameUtils";
import BackToGamesButton from "../components/games/BackToGamesButton";
import ChallengeBanner, { readChallengeTarget } from "../components/games/ChallengeBanner";
import { StarIcon, TrophyIcon, FlameIcon, BoltIcon } from "../components/games/ui-icons";
import "./WordWebDefenderPage.css";

const ART = "/assets/games/shape-web-defender";
const MAX_LIVES = 3;

const ZIPPY_POSES = {
  idle: `${ART}/swd_zippy_happy.png`,
  cheer: `${ART}/swd_zippy_cheer.png`,
  worried: `${ART}/swd_zippy_worried.png`,
  dizzy: `${ART}/swd_zippy_worried.png`,
};

const MOOD_LABEL = { idle: "Zippy", worried: "Eek!", cheer: "Yay!", dizzy: "Whoa!" };

const CONFETTI_BITS = Array.from({ length: 30 }, (_, index) => ({
  id: index,
  left: `${3 + ((index * 29) % 94)}%`,
  delay: `${(index % 8) * 0.08}s`,
  color: ["#8F38BF", "#B85CE0", "#FFD94D", "#ED5CA8", "#4F9CE8", "#ffffff"][index % 6],
}));

// MARK: - Shape glyph (white-rimmed, matches the iOS ShapeGlyph)

const ShapeGlyph = ({ shape, size = 40 }) => {
  const spec = shapeMeta(shape);
  const common = { fill: spec.color, stroke: "white", strokeWidth: 6, strokeLinejoin: "round" };
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className="wwd-glyph" aria-hidden="true">
      {shape === "circle" && <circle cx="50" cy="50" r="34" {...common} />}
      {shape === "triangle" && <polygon points="50,13 90,84 10,84" {...common} />}
      {shape === "square" && <rect x="15" y="15" width="70" height="70" rx="10" {...common} />}
      {shape === "star" && (
        <polygon points="50,7 61,36 93,37 68,57 76,89 50,71 24,89 32,57 7,37 39,36" {...common} />
      )}
      {shape === "heart" && (
        <path d="M50 88 C40 76 12 59 12 35 C12 16 36 10 50 29 C64 10 88 16 88 35 C88 59 60 76 50 88 Z" {...common} />
      )}
      {shape === "diamond" && <polygon points="50,7 91,50 50,93 9,50" {...common} />}
    </svg>
  );
};

// MARK: - Spider on the field (fluffy PNG + shape bubble)

const SpiderView = ({ spider }) => {
  const pos = spiderDisplayPosition(spider);
  const urgent = isUrgent(spider);
  return (
    <div
      className={`wwd-spider ${urgent ? "is-urgent" : ""} ${spider.isGolden ? "is-golden" : ""}`}
      style={{ left: `${pos.x}%`, top: `${pos.y}%`, "--sc": spider.bubbleScale }}
      aria-label={`${spider.shape} spider`}
    >
      <span className="wwd-shape-bubble">
        <ShapeGlyph shape={spider.shape} size={38 * spider.bubbleScale} />
      </span>
      <span className="wwd-spider-body">
        {spider.isGolden && <span className="wwd-gold-halo" aria-hidden="true" />}
        <img src={`${ART}/${spider.sprite}.png`} alt="" draggable="false" />
      </span>
      {urgent && <span className="wwd-danger-ping" aria-hidden="true">!</span>}
    </div>
  );
};

// MARK: - Shape tray tile

const TrayTile = ({ shape, index, wrong, matched, disabled, onChoose }) => {
  const spec = shapeMeta(shape.id);
  return (
    <motion.button
      type="button"
      className={`wwd-tray-tile ${wrong ? "is-wrong" : ""}`}
      style={{ "--tint": spec.color }}
      onClick={() => onChoose(shape.id)}
      disabled={disabled}
      aria-label={shape.label}
      initial={{ scale: 0.55, opacity: 0 }}
      animate={{ scale: matched ? 1.12 : 1, opacity: disabled ? 0.5 : 1 }}
      transition={
        matched
          ? { type: "spring", stiffness: 420, damping: 12 }
          : { type: "spring", stiffness: 260, damping: 18, delay: index * 0.05 }
      }
      whileTap={{ scale: 0.92 }}
    >
      <span className="wwd-tile-glyph">
        <ShapeGlyph shape={shape.id} size={40} />
        {matched && <span className="wwd-tile-ring" aria-hidden="true" />}
      </span>
      <small>{shape.label}</small>
    </motion.button>
  );
};

const ShapeWebDefenderPage = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { refreshUserData } = useAuth();
  const sounds = useShapeWebSound();
  const fieldShake = useAnimationControls();
  const shakeField = useCallback(
    (strong) =>
      fieldShake.start({
        x: strong ? [0, -11, 11, -8, 8, -4, 4, 0] : [0, -8, 8, -5, 5, 0],
        transition: { duration: strong ? 0.55 : 0.42 },
      }),
    [fieldShake]
  );

  // Render state.
  const [status, setStatus] = useState("intro"); // intro | countdown | playing | paused | gameover
  // A friend's beat-my-score link (?challenge=N) — read once on mount.
  const [challengeTarget] = useState(readChallengeTarget);
  const [spiders, setSpiders] = useState([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lives, setLives] = useState(MAX_LIVES);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [feedback, setFeedback] = useState("Tap a shape that matches any spider!");
  const [wrongShape, setWrongShape] = useState(null);
  const [matchedTray, setMatchedTray] = useState(null);
  const [banner, setBanner] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [burst, setBurst] = useState(null);
  const [zap, setZap] = useState(null);
  const [heroCelebration, setHeroCelebration] = useState(null);
  const [fleeing, setFleeing] = useState([]);
  const [lifeFlash, setLifeFlash] = useState(false);
  const [breachFlash, setBreachFlash] = useState(false);
  const [heartsBounce, setHeartsBounce] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [starsAwarded, setStarsAwarded] = useState(0);
  const [isNewBest, setIsNewBest] = useState(false);
  const [level, setLevel] = useState(() => readLevel());
  const [levelChange, setLevelChange] = useState(0);
  const [resultStats, setResultStats] = useState({ score: 0, best: 0, bestStreak: 0, time: "00:00", defended: 0 });
  const [localBest, setLocalBest] = useState(() => readLocalBest());

  // Physics / logic refs (never trigger renders directly).
  const statusRef = useRef("intro");
  const spidersRef = useRef([]);
  const elapsedRef = useRef(0);
  const elapsedSecondsRef = useRef(0);
  const spawnClockRef = useRef(0);
  const nextIdRef = useRef(3);
  const endedRef = useRef(false);
  const savedRef = useRef(false);
  const tuningRef = useRef(tuningForLevel(readLevel()));
  const levelRef = useRef(readLevel());
  const ratingsRef = useRef(readRatings());
  const scoreRef = useRef(0);
  const streakRef = useRef(0);
  const bestStreakRef = useRef(0);
  const defendedRef = useRef(0);
  const livesRef = useRef(MAX_LIVES);
  const localBestRef = useRef(readLocalBest());

  const timersRef = useRef([]);
  const rafRef = useRef(0);
  const lastFrameRef = useRef(0);
  const lifeLostRef = useRef(() => {});
  const breachRef = useRef(() => {});

  statusRef.current = status;

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  // Unmount cleanup ONLY. The `sounds` object gets a new identity whenever
  // mute is toggled, so listing it as a dependency made React run this
  // cleanup mid-round — cancelling the animation frame and freezing the whole
  // game. Read the latest sounds through a ref instead.
  const soundsRef = useRef(sounds);
  soundsRef.current = sounds;
  useEffect(
    () => () => {
      clearTimers();
      window.cancelAnimationFrame(rafRef.current);
      soundsRef.current.stop();
    },
    [clearTimers]
  );

  // MARK: - Progress persistence (best-effort, never blocks the result screen)

  const persist = useCallback(
    async (finalScore) => {
      if (savedRef.current || !apiClient) return;
      savedRef.current = true;
      try {
        const response = await apiClient.postGameProgress("WORD_WEB_DEFENDER", finalScore, true, null);
        setStarsAwarded(Number(response?.stars_awarded || 0));
        const serverBest = Number(response?.progress?.best_score ?? response?.best_score ?? NaN);
        // A 0-point round must never celebrate "New best!".
        if (finalScore > 0 && Number.isFinite(serverBest) && serverBest <= finalScore) setIsNewBest(true);
        if (response?.status || response?.success || response?.progress) {
          window.dispatchEvent(new CustomEvent("games:progress-updated"));
          refreshUserData?.();
        }
      } catch {
        // Saving must not block the result screen.
      }
    },
    [apiClient, refreshUserData]
  );

  // MARK: - Banner

  const showBanner = useCallback(
    (icon, text, tint) => {
      setBanner({ id: Date.now(), icon, text, tint });
      schedule(() => setBanner(null), 1300);
    },
    [schedule]
  );

  // MARK: - Life / breach handling

  const lifeLost = useCallback(
    (bannerText = "Careful!") => {
      sounds.play("wrong");
      setLifeFlash(true);
      shakeField(false);
      setHeartsBounce(true);
      if (livesRef.current === 1) {
        showBanner("♥", "Last life!", "#ED4C5C");
      } else {
        showBanner("!", bannerText, "#F2704D");
      }
      schedule(() => {
        setLifeFlash(false);
        setHeartsBounce(false);
      }, 340);
    },
    [sounds, showBanner, schedule, shakeField]
  );

  const handleBreach = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    setStatus("gameover");
    statusRef.current = "gameover";

    // Adaptive difficulty nudge for the NEXT round.
    const outcome = applyDifficultyAfterRound(levelRef.current, ratingsRef.current, defendedRef.current);
    levelRef.current = outcome.level;
    ratingsRef.current = outcome.ratings;
    writeLevel(outcome.level);
    writeRatings(outcome.ratings);
    setLevel(outcome.level);
    setLevelChange(outcome.change);

    const finalScore = scoreRef.current;
    const beatBest = finalScore > localBestRef.current;
    if (beatBest) {
      localBestRef.current = finalScore;
      writeLocalBest(finalScore);
      setLocalBest(finalScore);
      setIsNewBest(true);
    }
    setResultStats({
      score: finalScore,
      best: Math.max(localBestRef.current, finalScore),
      bestStreak: bestStreakRef.current,
      time: timeLabel(elapsedSecondsRef.current),
      defended: defendedRef.current,
    });

    sounds.play("breach");
    setBreachFlash(true);
    shakeField(true);
    persist(finalScore);

    schedule(() => {
      setBreachFlash(false);
      setShowResult(true);
      if (defendedRef.current >= 12 || beatBest) setConfettiKey((value) => value + 1);
    }, 900);
  }, [sounds, persist, schedule, shakeField]);

  lifeLostRef.current = lifeLost;
  breachRef.current = handleBreach;

  // MARK: - Game loop (mounted once; reads refs so it never restarts)

  useEffect(() => {
    const step = (ts) => {
      rafRef.current = window.requestAnimationFrame(step);
      if (statusRef.current !== "playing") {
        lastFrameRef.current = 0;
        return;
      }
      if (!lastFrameRef.current) lastFrameRef.current = ts;
      const dt = Math.min(0.05, (ts - lastFrameRef.current) / 1000);
      lastFrameRef.current = ts;
      elapsedRef.current += dt;
      spawnClockRef.current += dt;

      const secs = Math.floor(elapsedRef.current);
      if (secs !== elapsedSecondsRef.current) {
        elapsedSecondsRef.current = secs;
        setElapsedSeconds(secs);
      }

      // Advance every spider in place.
      const list = spidersRef.current;
      for (let i = 0; i < list.length; i += 1) list[i].progress += list[i].speed * dt;

      // Any spider that reached Zippy costs one life and leaves.
      if (list.some((s) => s.progress >= DANGER_PROGRESS)) {
        const survivors = list.filter((s) => s.progress < DANGER_PROGRESS);
        spidersRef.current = survivors;
        setSpiders([...survivors]);
        const nextLives = Math.max(0, livesRef.current - 1);
        livesRef.current = nextLives;
        setLives(nextLives);
        if (nextLives === 0) breachRef.current();
        else lifeLostRef.current();
        return;
      }

      // Spawn on the ramp, capped by the swarm size.
      if (
        spawnClockRef.current >= spawnInterval(tuningRef.current, elapsedRef.current) &&
        list.length < tuningRef.current.maxSpiders
      ) {
        spawnClockRef.current = 0;
        nextIdRef.current += 1;
        list.push(makeSpider(nextIdRef.current, elapsedRef.current, tuningRef.current));
      }
      setSpiders([...list]);
    };
    rafRef.current = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  // MARK: - Tray tap

  const scheduleFeedbackReset = useCallback(
    (text, clearCelebration) => {
      schedule(() => {
        setFeedback(text);
        setWrongShape(null);
        setBurst(null);
        setZap(null);
        setMatchedTray(null);
        if (clearCelebration) setHeroCelebration(null);
      }, 900);
    },
    [schedule]
  );

  const chooseShape = useCallback(
    (shapeId) => {
      if (statusRef.current !== "playing") return;
      const label = shapeMeta(shapeId).label;
      const candidates = spidersRef.current.filter((s) => s.shape === shapeId);
      const solved = candidates.reduce((best, s) => (!best || s.progress > best.progress ? s : best), null);

      if (!solved) {
        streakRef.current = 0;
        setStreak(0);
        setWrongShape(shapeId);

        // An empty field is a harmless whiff — there was nothing to read, so
        // it would be unfair to punish it (matches the iOS harmlessMiss).
        if (spidersRef.current.length === 0) {
          setFeedback(`No ${label.toLowerCase()} spider — try another!`);
          sounds.play("wrong");
          scheduleFeedbackReset("Look carefully and choose a matching shape!", false);
          return;
        }

        // Wrong shape with spiders on the field costs a life, and — same as a
        // breach — clears the board for a clean restart (matches iOS).
        spidersRef.current = [];
        setSpiders([]);
        const nextLives = Math.max(0, livesRef.current - 1);
        livesRef.current = nextLives;
        setLives(nextLives);
        setFeedback("Wrong shape — watch out!");
        if (nextLives === 0) {
          breachRef.current();
          return;
        }
        lifeLostRef.current("Wrong shape!");
        scheduleFeedbackReset("Look carefully and choose a matching shape!", false);
        return;
      }

      streakRef.current += 1;
      const nextStreak = streakRef.current;
      bestStreakRef.current = Math.max(bestStreakRef.current, nextStreak);
      let points = pointsForStreak(nextStreak);
      if (solved.isGolden) points *= 2;
      const closeCall = solved.progress > CLOSE_CALL_PROGRESS;
      if (closeCall) points += 10;
      scoreRef.current += points;
      defendedRef.current += 1;
      setStreak(nextStreak);
      setScore(scoreRef.current);

      const survivors = spidersRef.current.filter((s) => s.id !== solved.id);
      spidersRef.current = survivors;
      setSpiders([...survivors]);

      const target = spiderDisplayPosition(solved);
      sounds.play("correct");
      setWrongShape(null);
      setFeedback(`Great match — ${label}!`);
      setZap({ id: solved.id, kind: shapeId, target });
      setHeroCelebration({ id: Date.now(), shape: shapeId });
      setMatchedTray(shapeId);

      const ghostId = solved.id;
      setFleeing((current) => [...current, { id: ghostId, spider: solved, from: target, to: spiderFleePoint(solved) }]);
      schedule(() => {
        setZap(null);
        setBurst({ id: ghostId, x: target.x, y: target.y, points, streak: nextStreak });
      }, 200);
      schedule(() => setFleeing((current) => current.filter((g) => g.id !== ghostId)), 1000);
      schedule(() => setBurst(null), 1000);

      if (nextStreak >= 5 && nextStreak % 5 === 0) {
        sounds.play("blast");
        showBanner(<FlameIcon size={18} />, `Combo ×${nextStreak}!`, "#F5731F");
      } else if (solved.isGolden) {
        sounds.play("victory");
        showBanner("✦", "Golden spider! ×2", "#E8A21F");
      } else if (closeCall) {
        sounds.play("blast");
        showBanner(<BoltIcon size={18} />, "Just in time!", "#40B37A");
      }
      scheduleFeedbackReset("Tap another matching shape!", true);
    },
    [sounds, schedule, scheduleFeedbackReset, showBanner]
  );

  // MARK: - Round lifecycle

  const begin = useCallback(() => {
    tuningRef.current = tuningForLevel(levelRef.current);
    elapsedRef.current = 0;
    elapsedSecondsRef.current = 0;
    spawnClockRef.current = 0;
    nextIdRef.current = tuningRef.current.openingCount;
    endedRef.current = false;
    savedRef.current = false;
    scoreRef.current = 0;
    streakRef.current = 0;
    bestStreakRef.current = 0;
    defendedRef.current = 0;
    livesRef.current = MAX_LIVES;
    lastFrameRef.current = 0;

    const opening = openingSpiders(tuningRef.current);
    spidersRef.current = opening;
    setSpiders([...opening]);
    setScore(0);
    setStreak(0);
    setLives(MAX_LIVES);
    setElapsedSeconds(0);
    setStatus("playing");
    statusRef.current = "playing";
  }, []);

  const startRound = useCallback(() => {
    clearTimers();
    setStarsAwarded(0);
    setIsNewBest(false);
    setLevelChange(0);
    setWrongShape(null);
    setBurst(null);
    setZap(null);
    setHeroCelebration(null);
    setFleeing([]);
    setBanner(null);
    setMatchedTray(null);
    setBreachFlash(false);
    setLifeFlash(false);
    setShowResult(false);
    setFeedback("Tap a shape that matches any spider!");
    spidersRef.current = [];
    setSpiders([]);
    setStatus("countdown");
    statusRef.current = "countdown";

    // Ready… set… GO!
    [3, 2, 1].forEach((value, index) => {
      schedule(() => {
        setCountdown(value);
        sounds.play("tap");
      }, index * 620);
    });
    schedule(() => {
      setCountdown(null);
      sounds.play("blast");
      begin();
    }, 3 * 620);
  }, [clearTimers, schedule, sounds, begin]);

  const togglePause = useCallback(() => {
    setStatus((current) => {
      if (current === "playing") {
        statusRef.current = "paused";
        return "paused";
      }
      if (current === "paused") {
        lastFrameRef.current = 0;
        statusRef.current = "playing";
        return "playing";
      }
      return current;
    });
  }, []);

  const handleExit = useCallback(() => {
    clearTimers();
    sounds.stop();
    navigate("/games");
  }, [clearTimers, sounds, navigate]);

  // MARK: - Derived

  const hasUrgent = status === "playing" && spiders.some(isUrgent);
  const zippyMood =
    status === "gameover" ? "dizzy" : heroCelebration ? "cheer" : hasUrgent ? "worried" : "idle";
  const heartsSlots = Array.from({ length: MAX_LIVES }, (_, i) => i < lives);
  const tuningName = tuningForLevel(level).name;

  // The result modal only shows after a breach (the spiders reached Zippy),
  // so the celebration must scale with what the child actually did — the old
  // fixed "Awesome job! ...kept Zippy safe" praised 0-match losses.
  const resultRating =
    resultStats.defended >= 12 ? 3 : resultStats.defended >= 6 ? 2 : resultStats.defended >= 1 ? 1 : 0;
  const resultTitle =
    resultRating === 3 ? "Awesome job!" : resultRating === 2 ? "Great defending!" : resultRating === 1 ? "Good try!" : "The spiders got through!";
  const resultCopy =
    resultStats.defended > 0
      ? `You bounced ${resultStats.defended} ${resultStats.defended === 1 ? "spider" : "spiders"} away and kept Zippy safe for ${resultStats.time}.`
      : `Zippy lasted ${resultStats.time}. Tap the shape that matches a spider to bounce it away!`;

  return (
    <div className="wwd-page">
      <ChallengeBanner target={challengeTarget} score={score} ended={status === "gameover"} />
      <div className="wwd-shell">
        {/* Header bar kept exactly as before. */}
        <header className="wwd-header">
          <BackToGamesButton onClick={handleExit} accent="#6b559a" />
          <div className="wwd-title-lockup">
            <span className="wwd-logo-mark">◆</span>
            <div>
              <h1>Shape Web Defender</h1>
              <p>Match shapes. Save the playground!</p>
            </div>
          </div>
          <div className="wwd-header-actions">
            <div className="wwd-stat-chip">
              <small>Score</small>
              <strong>{score}</strong>
            </div>
            <div className="wwd-stat-chip wwd-time-chip">
              <small>Time</small>
              <strong>{timeLabel(elapsedSeconds)}</strong>
            </div>
            <button
              type="button"
              className="wwd-icon-button"
              onClick={sounds.toggleMute}
              aria-label={sounds.muted ? "Turn sound on" : "Mute sound"}
            >
              {sounds.muted ? "♩" : "♪"}
            </button>
            <button
              type="button"
              className="wwd-icon-button"
              onClick={togglePause}
              aria-label={status === "paused" ? "Resume game" : "Pause game"}
            >
              {status === "paused" ? "▶" : "Ⅱ"}
            </button>
          </div>
        </header>

        <main className="wwd-game-card">
          <motion.div className="wwd-playground" animate={fieldShake}>
            <img className="wwd-bg" src={`${ART}/swd_bg_playground.jpg`} alt="" aria-hidden="true" draggable="false" />

            {/* Chalk play circle + safe-zone ring. */}
            <div className="wwd-chalk-circle" aria-hidden="true" />
            <div className="wwd-safe-ring" aria-hidden="true" />

            {/* Silk threads back to each spider's entry point. */}
            <svg className="wwd-threads" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {spiders.map((spider) => {
                const pos = spiderDisplayPosition(spider);
                return (
                  <line
                    key={spider.id}
                    x1={spider.startX}
                    y1={spider.startY}
                    x2={pos.x}
                    y2={pos.y}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="0.3"
                  />
                );
              })}
            </svg>

            {/* Zippy in the middle of the field. */}
            <div className={`wwd-zippy wwd-zippy-${zippyMood}`} style={{ left: `${CENTER.x}%`, top: `${CENTER.y}%` }}>
              <span className="wwd-zippy-glow" aria-hidden="true" />
              <span className="wwd-zippy-shadow" aria-hidden="true" />
              <img
                key={zippyMood}
                src={ZIPPY_POSES[zippyMood]}
                alt="Zippy, your playground defender"
                draggable="false"
              />
              {heroCelebration && (
                <span key={heroCelebration.id} className="wwd-zippy-rise" aria-hidden="true">
                  <ShapeGlyph shape={heroCelebration.shape} size={46} />
                </span>
              )}
              <span className="wwd-zippy-label">{MOOD_LABEL[zippyMood]}</span>
            </div>

            {/* Live spiders. */}
            {spiders.map((spider) => (
              <SpiderView key={spider.id} spider={spider} />
            ))}

            {/* Shooed spiders swinging away. */}
            <AnimatePresence>
              {fleeing.map((ghost) => (
                <motion.div
                  key={`flee-${ghost.id}`}
                  className="wwd-flee"
                  style={{ "--sc": ghost.spider.bubbleScale }}
                  initial={{ left: `${ghost.from.x}%`, top: `${ghost.from.y}%`, rotate: 0, scale: 1.05, opacity: 1 }}
                  animate={{
                    left: `${ghost.to.x}%`,
                    top: `${ghost.to.y}%`,
                    rotate: ghost.id % 2 === 0 ? 480 : -480,
                    scale: 0.15,
                    opacity: 0,
                  }}
                  transition={{ duration: 0.7, ease: "easeIn" }}
                >
                  <img src={`${ART}/${ghost.spider.sprite}.png`} alt="" draggable="false" />
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Zippy's thrown shape flying at the matched spider. */}
            <AnimatePresence>
              {zap && (
                <motion.div
                  key={`zap-${zap.id}`}
                  className="wwd-zap"
                  initial={{ left: `${CENTER.x}%`, top: `${CENTER.y}%`, rotate: 0, scale: 0.5 }}
                  animate={{ left: `${zap.target.x}%`, top: `${zap.target.y}%`, rotate: 340, scale: 1.05 }}
                  transition={{ duration: 0.2, ease: "easeIn" }}
                >
                  <ShapeGlyph shape={zap.kind} size={34} />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Match burst + points. */}
            <AnimatePresence>
              {burst && (
                <motion.div
                  key={`burst-${burst.id}`}
                  className="wwd-burst"
                  style={{ left: `${burst.x}%`, top: `${burst.y}%` }}
                  initial={{ y: 0, opacity: 1, scale: 0.5 }}
                  animate={{ y: -46, opacity: 0, scale: 1 }}
                  transition={{ duration: 0.85, ease: "easeOut" }}
                >
                  <span className="wwd-burst-points">+{burst.points}</span>
                  {burst.streak >= 3 && <span className="wwd-burst-streak"><FlameIcon size={13} /> ×{burst.streak}</span>}
                  {[0, 1, 2, 3, 4, 5].map((i) => {
                    const angle = (i / 6) * Math.PI * 2;
                    return (
                      <span
                        key={i}
                        className="wwd-burst-spark"
                        style={{ "--dx": `${Math.cos(angle) * 46}px`, "--dy": `${Math.sin(angle) * 46}px` }}
                      />
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Floating HUD badges. */}
            <div className={`wwd-lives ${heartsBounce ? "is-bounce" : ""}`} aria-label={`${lives} lives left`}>
              {heartsSlots.map((filled, i) => (
                <span key={i} className={filled ? "on" : ""} aria-hidden="true">
                  {filled ? "♥" : "♡"}
                </span>
              ))}
            </div>
            <div className={`wwd-streak-badge ${streak >= 3 ? "is-hot" : ""}`}>
              <span aria-hidden="true"><FlameIcon size={18} /></span>
              <div>
                <small>Streak</small>
                <strong>×{streak}</strong>
              </div>
            </div>

            {/* Danger vignette. */}
            <div className={`wwd-danger-vignette ${hasUrgent ? "is-active" : ""}`} aria-hidden="true" />
            {/* Damage / breach red wash. */}
            <div
              className="wwd-red-wash"
              style={{ opacity: breachFlash ? 0.3 : lifeFlash ? 0.16 : 0 }}
              aria-hidden="true"
            />

            {/* Celebration banner. */}
            <AnimatePresence>
              {banner && (
                <motion.div
                  key={banner.id}
                  className="wwd-banner"
                  style={{ "--tint": banner.tint }}
                  initial={{ scale: 0.5, opacity: 0, rotate: -7 }}
                  animate={{ scale: 1, opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ type: "spring", stiffness: 320, damping: 15 }}
                >
                  <span className="wwd-banner-icon">{banner.icon}</span>
                  <strong>{banner.text}</strong>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 3-2-1 countdown. */}
            <AnimatePresence>
              {countdown != null && (
                <motion.div className="wwd-countdown" key={countdown} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <motion.span
                    key={`n-${countdown}`}
                    initial={{ scale: 2.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 240, damping: 14 }}
                  >
                    {countdown}
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Shape tray. */}
          <div className={`wwd-tray ${wrongShape ? "is-wrong" : ""}`}>
            <div className="wwd-tray-copy">
              <span>Find a match</span>
              <strong key={feedback}>{feedback}</strong>
            </div>
            <div className="wwd-tray-grid">
              {SHAPES.map((shape, index) => (
                <TrayTile
                  key={shape.id}
                  shape={shape}
                  index={index}
                  wrong={wrongShape === shape.id}
                  matched={matchedTray === shape.id}
                  disabled={status !== "playing"}
                  onChoose={chooseShape}
                />
              ))}
            </div>
          </div>
        </main>
      </div>

      {/* Full-width confetti. */}
      {confettiKey > 0 && (
        <div className="wwd-confetti-layer" key={confettiKey} aria-hidden="true">
          {CONFETTI_BITS.map((bit) => (
            <i key={bit.id} className="wwd-confetti" style={{ left: bit.left, animationDelay: bit.delay, background: bit.color }} />
          ))}
        </div>
      )}

      {/* Intro overlay. */}
      <AnimatePresence>
        {status === "intro" && (
          <motion.div className="wwd-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="wwd-modal wwd-intro-modal"
              initial={{ scale: 0.9, y: 22, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 20 }}
            >
              <div className="wwd-intro-hero">
                <img src={`${ART}/swd_cover.jpg`} alt="" aria-hidden="true" draggable="false" />
              </div>
              <div className="wwd-intro-body">
                <span className="wwd-eyebrow">Let's match shapes!</span>
                <h2>Protect Zippy!</h2>
                <p className="wwd-modal-subtitle">Tap the matching shape to bounce the silly spiders away!</p>
                <div className="wwd-how-steps">
                  <div>
                    <span>1</span>
                    <strong>Watch the spiders</strong>
                    <small>Each spider carries a fun shape.</small>
                  </div>
                  <div>
                    <span>2</span>
                    <strong>Find the same shape</strong>
                    <small>Tap the matching shape below.</small>
                  </div>
                  <div>
                    <span>3</span>
                    <strong>Keep Zippy safe!</strong>
                    <small>You have 3 lives. Don't let the spiders reach Zippy!</small>
                  </div>
                </div>
                {localBest > 0 && (
                  <div className="wwd-intro-chips">
                    <div className="wwd-best-pill"><TrophyIcon size={17} /> Best: {localBest}</div>
                  </div>
                )}
                <button type="button" className="wwd-primary-button" onClick={startRound}>
                  {localBest > 0 ? "Play again" : "Let's play!"} <span>→</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pause overlay. */}
      <AnimatePresence>
        {status === "paused" && (
          <motion.div className="wwd-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="wwd-modal wwd-pause-modal"
              initial={{ scale: 0.9, y: 22, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 20 }}
            >
              <img className="wwd-pause-zippy" src={`${ART}/swd_zippy_wave.png`} alt="" aria-hidden="true" draggable="false" />
              <h2>Game paused</h2>
              <p className="wwd-modal-subtitle">The spiders are waiting patiently.</p>
              <button type="button" className="wwd-primary-button" onClick={togglePause}>
                Keep playing <span>▶</span>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result overlay. */}
      <AnimatePresence>
        {status === "gameover" && showResult && (
          <motion.div className="wwd-scrim" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className="wwd-modal wwd-result-modal"
              initial={{ scale: 0.85, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 18 }}
            >
              <div className="wwd-result-band">
                <motion.span
                  className="wwd-result-icon"
                  animate={{ y: [-5, 5], rotate: [-4, 4] }}
                  transition={{ duration: 1.8, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                >
                  <img
                    src={`${ART}/${resultRating >= 2 ? "swd_zippy_cheer" : "swd_zippy_worried"}.png`}
                    alt=""
                    className="h-28 w-28 object-contain"
                    style={{ filter: "drop-shadow(0 8px 14px rgba(50, 20, 80, 0.35))" }}
                    draggable={false}
                  />
                </motion.span>
              </div>
              <div className="wwd-result-body">
                <h2>{resultTitle}</h2>
                <p className="wwd-modal-subtitle">{resultCopy}</p>

                {isNewBest && <div className="wwd-newbest-pill">✦ New best!</div>}

                {/* Performance rating out of 3 (unearned stars stay dim);
                    the star CURRENCY payout is the separate line below. */}
                <div className="wwd-reward-badge" aria-label={`${resultRating} of 3 stars`}>
                  <span className="wwd-reward-stars">
                    {[0, 1, 2].map((i) => (
                      <i key={i} className={i < resultRating ? "on" : ""}>
                        ★
                      </i>
                    ))}
                  </span>
                  {starsAwarded > 0 && <strong>+{starsAwarded} stars earned</strong>}
                </div>

                <div className="wwd-result-stats">
                  <div>
                    <span><StarIcon size={12} /> Score</span>
                    <strong>{resultStats.score}</strong>
                  </div>
                  <div>
                    <span><TrophyIcon size={12} /> Best</span>
                    <strong>{resultStats.best}</strong>
                  </div>
                  <div>
                    <span><FlameIcon size={12} /> Best streak</span>
                    <strong>×{resultStats.bestStreak}</strong>
                  </div>
                  <div>
                    <span>⏱ Time survived</span>
                    <strong>{resultStats.time}</strong>
                  </div>
                </div>

                {levelChange > 0 && (
                  <div className="wwd-level-msg is-up">↗ Leveling up to {tuningName}!</div>
                )}
                {levelChange < 0 && (
                  <div className="wwd-level-msg is-down">Easing to {tuningName} — you&apos;ve got this!</div>
                )}

                <div className="wwd-result-buttons">
                  <button type="button" className="wwd-secondary-button" onClick={handleExit}>
                    All done
                  </button>
                  <button type="button" className="wwd-primary-button" onClick={startRound}>
                    Play again <span>↻</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShapeWebDefenderPage;
